import get from 'lodash/get';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {connect,useDispatch,useSelector} from 'react-redux';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import Stack from "@mui/material/Stack";

import useRequirePatientOnboarding from '../hooks/useRequirePatientOnboarding';
import getToken from '../redux/selectors/getToken';
import {logout} from '../redux/reducers/auth';

import './AppDashboard.scss';
import FlashError from '../components/common/ErrorFlash';
import Navbar from '../components/common/Navbar';
import LoadingScreen from '../components/common/LoadingScreen';
import BlankContent from '../components/common/BlankContent';
import BackButton from "../components/common/BackButton";
import ImpersonationBanner from "../components/auth/ImpersonationBanner";
import UpdatePaymentBanner from '../components/payments/UpdatePaymentBanner';
import VersionBanner from '../components/common/VersionBanner';

import useScrollToTop from '../hooks/useScrollToTop';
import useUser from '../hooks/useUser';
import useIsMobile from '../hooks/useIsMobile';
import { SIDEBAR_WIDTH, SIDEBAR_CLOSED_WIDTH} from '../config/constants';
import {setMenuOpenState} from "../redux/reducers/menu";

const PageContent = props => {
  try {
    if (props?.loading) return <LoadingScreen />;
    return <>{props.children}</>;
  } catch (e) {
    console.log('FATAL EXCEPTION');
    console.error(e);
    return (
      <Alert
        severity='error'
      >
        There was an error loading this page.
      </Alert>
    );
  }
};

const handleLogout = ({logout, dispatch}) => e => {
  dispatch(logout());
};

const AppDashboard = props => {
  useRequirePatientOnboarding();
  useScrollToTop();
  const {showBackBtn = false} = props;

  const user = useUser();
  const isMenuOpenPreState = useSelector((state) => state.menu.isMenuOpenPreState ?? true);
  const [isOpen, setIsOpen] = useState(isMenuOpenPreState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const accessToken = useSelector(getToken());
  const leftDistance = !isMobile ? (isOpen ? SIDEBAR_WIDTH : SIDEBAR_CLOSED_WIDTH) : 0;

  const onBack = () => navigate(-1);

  useEffect(() => {
    if (!accessToken && !props.skipAuth) {
      console.log('AppDashboard redirecting to login from', window.location.pathname);
      navigate('/login');
    }
  }, [accessToken, navigate, props.skipAuth]);

  useEffect(() => {
    dispatch(setMenuOpenState(isOpen));
  }, [dispatch, isOpen]);

  return (
    <Box className="AppDashboard">
      <Box style={{ marginLeft: leftDistance}}>
        <Navbar
          title={props.title}
          logout={handleLogout({dispatch, logout})}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          isMobile={isMobile}
          user={user}
          showUser={props.showUser}
          leftDistance={leftDistance}
          onBack={props.onBack}
          hideBack={props.hideBack}
        />
        <FlashError error={props.error} />
        <ImpersonationBanner />
        <VersionBanner />
        {user?.patient && <UpdatePaymentBanner user={user} />}
        {props.workInProgress
          ? <Alert severity='warning'>
            🚧 Please do not QA this page yet. It is still a work in progress.
          </Alert>
          : null
        }

        {
          props.warning
          ? <Alert severity='warning'>
            <AlertTitle>Warning</AlertTitle>
            {get(props, 'warning.message', 'Unknown warning')}
          </Alert>
          : null
        }

        {props.hasNoData
          ? <BlankContent
            title={props.blankContentMessage}
            Icon={ContactSupportIcon}
            ButtonComponent={
              <Button
                variant='outlined'
                onClick={() => navigate('/')}
              >
                Return to Home Screen
              </Button>
            }
          />
          : <Container className='ContainerContent' maxWidth='false'>
            {showBackBtn && (
              <Stack alignItems='flex-start'>
                <BackButton/>
              </Stack>
            )}
            <PageContent {...props} />
          </Container>
        }
      </Box>
    </Box>
  );
};

AppDashboard.defaultProps = {
  backgroundColor: '#F9F9F9',
  blankContentMessage: 'Your data could not be loaded',
  skipAuth: false,
  hideBack: false,
};

AppDashboard.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  loading: PropTypes.bool,
};

const mapState = state => ({
})

const mapDispatch = dispatch => ({
});

export default connect(mapState, mapDispatch)(AppDashboard);
